import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  Dumbbell,
  Eye,
  Flag,
  History,
  PencilLine,
} from "lucide-react";
import type { StudentExecutionItem, StudentWorkoutItem } from "@/lib/types";
import { ExerciseThumb, itemHasMedia } from "./ExerciseThumb";
import { Chip, Prescription } from "./Prescription";
import { parseWorkoutDetail } from "./workoutDetail";

type RowStatus = "pending" | "in_progress" | "completed";

function deriveStatus(existing: StudentExecutionItem | null): RowStatus {
  if (!existing) return "pending";
  if (existing.completed_at) return "completed";
  return "in_progress";
}

interface ExerciseRowProps {
  item: StudentWorkoutItem;
  existing: StudentExecutionItem | null;
  disabled: boolean;
  slug: string;
  onEdit: () => void;
  onPreview: () => void;
}

/**
 * Card de um exercício na lista de execução. Estado visual por status
 * (pendente / em andamento / concluído). Portado de `ItemRow` +
 * `RegisteredSummary` do legado `WorkoutExecutionView.tsx`.
 */
export function ExerciseRow({ item, existing, disabled, slug, onEdit, onPreview }: ExerciseRowProps) {
  const status = deriveStatus(existing);
  const parsed = parseWorkoutDetail(item.detail);
  const hasMedia = itemHasMedia(item);

  const style =
    status === "completed"
      ? {
          border: "border-accent-soft/45",
          ring: "ring-1 ring-accent-soft/15",
          bg: "bg-surface/85",
          icon: <CheckCircle2 size={13} aria-hidden />,
          label: "Concluído",
          labelColor: "text-accent-soft",
          buttonClass:
            "border-accent-soft/40 bg-accent/15 text-accent-soft hover:bg-accent/25",
          buttonLabel: "Editar registro",
        }
      : status === "in_progress"
        ? {
            border: "border-amber-400/50",
            ring: "ring-1 ring-amber-400/15",
            bg: "bg-surface/85",
            icon: <PencilLine size={13} aria-hidden />,
            label: "Em andamento",
            labelColor: "text-amber-400/90",
            buttonClass: "border-amber-400/40 bg-amber-400/10 text-amber-300 hover:bg-amber-400/15",
            buttonLabel: "Continuar registro",
          }
        : {
            border: "border-border-subtle",
            ring: "",
            bg: "bg-surface/65",
            icon: <Circle size={13} aria-hidden />,
            label: "Não feito",
            labelColor: "text-faint",
            buttonClass: "border-accent-soft/40 bg-accent text-ink hover:bg-accent-hover",
            buttonLabel: "Registrar",
          };

  return (
    <article
      className={[
        "rounded-2xl border p-3 backdrop-blur-sm sm:p-4",
        "transition-[border-color,background-color,box-shadow] duration-300 ease-out",
        style.border,
        style.ring,
        style.bg,
      ].join(" ")}
      data-status={status}
    >
      <div className="flex gap-3">
        <div className="shrink-0">
          <ExerciseThumb item={item} onClick={hasMedia ? onPreview : undefined} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h5 className="text-base font-medium text-ink">
                <span className="border-b border-accent-soft/35 pb-0.5">{item.line}</span>
              </h5>
              <span
                className={[
                  "inline-flex shrink-0 items-center gap-1 text-[11px] font-medium uppercase tracking-wider transition-colors duration-300",
                  style.labelColor,
                ].join(" ")}
                aria-label={`Status: ${style.label}`}
              >
                {style.icon}
                <span className="hidden sm:inline">{style.label}</span>
              </span>
            </div>

            <Prescription parsed={parsed} />
          </div>

          {existing && <RegisteredSummary existing={existing} />}

          <div className="mt-1 flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex flex-wrap items-center gap-2">
              {hasMedia && (
                <button
                  type="button"
                  onClick={onPreview}
                  disabled={disabled}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-elevated px-2.5 py-1 text-xs font-medium text-muted transition-colors hover:border-border-strong hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Ver demonstração de ${item.line}`}
                >
                  <Eye size={12} aria-hidden />
                  Demonstração
                </button>
              )}
              <Link
                to={`/s/${slug}/historico`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-elevated px-2.5 py-1 text-xs font-medium text-muted transition-colors hover:border-border-strong hover:text-ink"
                aria-label={`Histórico de ${item.line}`}
              >
                <History size={12} aria-hidden />
                Histórico
              </Link>
            </div>
            <button
              type="button"
              onClick={onEdit}
              disabled={disabled}
              className={[
                "inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-medium shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                style.buttonClass,
              ].join(" ")}
            >
              <PencilLine size={13} aria-hidden />
              {style.buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/** Resumo do que o aluno registrou — chips accent pra distinguir do prescrito. */
function RegisteredSummary({ existing }: { existing: StudentExecutionItem }) {
  const repsLabel = existing.reps_per_set?.length ? existing.reps_per_set.join(" / ") : null;

  const hasAnyData =
    existing.sets_done != null ||
    repsLabel != null ||
    existing.load_kg != null ||
    existing.rpe != null ||
    !!existing.felt ||
    !!existing.notes;

  if (!hasAnyData) return null;

  const feltLabel =
    existing.felt === "easy"
      ? "Tranquilo"
      : existing.felt === "medium"
        ? "OK"
        : existing.felt === "hard"
          ? "Difícil"
          : null;

  return (
    <div className="animate-fade-in rounded-lg border border-accent-soft/25 bg-accent/10 p-2">
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-accent-soft">
        Você registrou
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {existing.sets_done != null && (
          <Chip
            icon={<CheckCircle2 size={11} aria-hidden />}
            label={`${existing.sets_done} ${existing.sets_done === 1 ? "série" : "séries"}`}
            tone="accent"
          />
        )}
        {repsLabel && (
          <Chip icon={<Dumbbell size={11} aria-hidden />} label={`${repsLabel} reps`} tone="accent" />
        )}
        {existing.load_kg != null && (
          <Chip icon={<Dumbbell size={11} aria-hidden />} label={`${existing.load_kg} kg`} tone="accent" />
        )}
        {existing.rpe != null && (
          <Chip icon={<Flag size={11} aria-hidden />} label={`RPE ${existing.rpe}`} tone="accent" />
        )}
        {feltLabel && (
          <Chip icon={<CheckCircle2 size={11} aria-hidden />} label={feltLabel} tone="accent" />
        )}
      </div>
      {existing.notes && (
        <p className="mt-1.5 line-clamp-2 text-xs italic text-muted">“{existing.notes}”</p>
      )}
    </div>
  );
}
